/**
 * variant-fabric-picker.js
 * ──────────────────────────────────────────────────────────────────────────────
 * Stoff-Swatch Picker für Prestige Theme.
 *
 * Strategie: Wir triggern den nativen <input type="radio"> des Themes.
 * Prestige reagiert darauf automatisch: Gallery, Preis, Availability.
 * Wir kümmern uns nur um:
 *   1) Inline-Swatches neu sortieren (gewählter Stoff → Position 1)
 *   2) variant-picker__option-info <span> aktualisieren (Stoffname-Label)
 *   3) Drawer öffnen/schließen
 *   4) Drawer-Grid is-selected States
 */

(function () {
  'use strict';

  const SEL_PICKER    = '[data-fabric-picker]';
  const SEL_INLINE    = '[data-fabric-swatches-inline]';
  const SEL_MORE      = '[data-fabric-more-trigger]';
  const SEL_DRAWER    = '[data-fabric-drawer]';
  const SEL_DRAWER_IT = '[data-fabric-drawer-item]';

  /* ─── Prestige x-modal öffnen / schließen ─────────────────────────────── */

  function openModal(el) {
    if (!el) return;
    if (typeof el.show === 'function') {
      el.show();
    } else {
      el.setAttribute('open', '');
      el.classList.add('is-open');
      lockScroll();
    }
  }

  function closeModal(el) {
    if (!el) return;
    if (typeof el.hide === 'function') {
      el.hide();
    } else {
      el.removeAttribute('open');
      el.classList.remove('is-open');
      unlockScroll();
    }
  }

  /* ─── Scroll Lock ─────────────────────────────────────────────────────── */

  function lockScroll() {
    if (document.body.dataset.fpLocked) return;
    const y = window.scrollY;
    document.body.style.cssText += `position:fixed;top:-${y}px;width:100%`;
    document.body.dataset.fpScrollY = y;
    document.body.dataset.fpLocked  = '1';
  }

  function unlockScroll() {
    if (!document.body.dataset.fpLocked) return;
    const y = parseInt(document.body.dataset.fpScrollY || '0', 10);
    document.body.style.position = '';
    document.body.style.top      = '';
    document.body.style.width    = '';
    delete document.body.dataset.fpLocked;
    window.scrollTo(0, y);
  }

  /* ─── option-info <span> aktualisieren ───────────────────────────────── */
  /**
   * Sucht den <span> der neben <legend>Stoff:</legend> steht und setzt
   * den neuen Stoffnamen.
   *
   * DOM-Struktur im Theme:
   *   .variant-picker__option-info
   *     .h-stack
   *       <legend>Stoff:</legend>
   *       <span>Classico 01</span>
   */
  function updateOptionInfoLabel(pickerEl, fabricName) {
    // Gehe zum variant-picker__option Wrapper (ein oder zwei Ebenen hoch)
    const optionBlock = pickerEl.closest('[data-option-index], .variant-picker__option-values')
                        ?.closest('.variant-picker__option, [class*="variant-picker"]')
                        || pickerEl.closest('fieldset');

    if (!optionBlock) return;

    // Suche den <span> der direkt nach einer <legend> mit "Stoff" steht
    const legends = optionBlock.querySelectorAll('legend');
    for (const legend of legends) {
      if (legend.textContent.trim().replace(':', '') === 'Stoff') {
        const span = legend.nextElementSibling;
        if (span && span.tagName === 'SPAN') {
          span.textContent = fabricName;
          return;
        }
      }
    }

    // Fallback: Suche in der übergeordneten Section
    const section = pickerEl.closest('section, [data-section-id]') || document;
    const allLegends = section.querySelectorAll('.variant-picker__option-info legend');
    for (const legend of allLegends) {
      if (legend.textContent.trim().replace(':', '') === 'Stoff') {
        const span = legend.nextElementSibling;
        if (span && span.tagName === 'SPAN') {
          span.textContent = fabricName;
        }
        return;
      }
    }
  }

  /* ─── Nativen Radio-Input triggern ───────────────────────────────────── */

  function triggerNativeRadio(pickerEl, fabricName) {
    const paramName = pickerEl.dataset.paramName;
    if (!paramName) return;

    // Wert kann option_value.id (Zahl/UUID) oder der Name selbst sein.
    // Wir suchen nach value=fabricName (unser Snippet setzt value="{{ fabric_name }}")
    const radio = pickerEl.querySelector(
      `input[type="radio"][name="${CSS.escape(paramName)}"][value="${CSS.escape(fabricName)}"]`
    );
    if (!radio || radio.checked) return; // bereits ausgewählt

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    radio.dispatchEvent(new Event('input',  { bubbles: true }));
  }

  /* ─── Inline-Swatches neu aufbauen ──────────────────────────────────── */

  function rebuildInlineSwatches(pickerEl, selectedFabric) {
    const allSwatches = JSON.parse(decodeURIComponent(pickerEl.dataset.swatchData || '[]'));
    const visible     = parseInt(pickerEl.dataset.swatchesVisible, 10) || 4;
    const inlineRow   = pickerEl.querySelector(SEL_INLINE);
    if (!inlineRow) return;

    // Gewählten Stoff nach vorne
    const sel     = allSwatches.find(s => s.name === selectedFabric);
    const rest    = allSwatches.filter(s => s.name !== selectedFabric);
    const ordered = sel ? [sel, ...rest] : allSwatches;

    const visibleItems = ordered.slice(0, visible);
    const hiddenCount  = ordered.length - visible;

    // Bestehende Labels (Swatches) + More-Button entfernen, Radio-Inputs behalten
    inlineRow.querySelectorAll('label[data-fabric-swatch], button.fabric-swatch--more').forEach(el => el.remove());

    // Neue Labels einfügen (nach dem letzten Radio-Input)
    visibleItems.forEach((swatch, i) => {
      const isSelected = swatch.name === selectedFabric;

      // Zugehörigen Radio-Input finden (über den Stoffnamen im value)
      const radio = inlineRow.querySelector(
        `input[type="radio"][value="${CSS.escape(swatch.name)}"]`
      );
      const radioId = radio ? radio.id : `${pickerEl.dataset.paramName}-fp-${i}`;

      const label = document.createElement('label');
      label.setAttribute('for', radioId);
      label.className = 'thumbnail-swatch'
        + (isSelected ? ' is-selected' : '');
      label.dataset.fabricSwatch = swatch.name;
      label.dataset.variantId    = swatch.variantId || '';
      label.setAttribute('title', swatch.name);

      const sr = document.createElement('span');
      sr.className = 'sr-only';
      sr.textContent = swatch.name;
      label.appendChild(sr);

      if (swatch.image) {
        const img = document.createElement('img');
        img.src     = swatch.image;
        img.alt     = swatch.name;
        img.width   = 68;
        img.height  = 68;
        img.setAttribute('sizes', '(max-width: 699px) 48px, 68px');
        img.loading = 'lazy';
        img.className = 'object-cover';
        label.appendChild(img);
      } else {
        const fb = document.createElement('span');
        fb.className = 'fabric-swatch__fallback';
        fb.textContent = swatch.name.slice(0, 2).toUpperCase();
        label.appendChild(fb);
      }

      inlineRow.appendChild(label);
    });

    // "+N mehr" Button
    if (hiddenCount > 0) {
      const btn = document.createElement('button');
      btn.type      = 'button';
      btn.className = 'thumbnail-swatch fabric-swatch--more';
      btn.dataset.fabricMoreTrigger = '';
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.setAttribute('aria-label', `${hiddenCount} weitere Stoffe anzeigen`);
      btn.setAttribute('title', `${hiddenCount} weitere Stoffe`);

      const countSpan = document.createElement('span');
      countSpan.className = 'fabric-swatch__more-count';
      countSpan.textContent = `+${hiddenCount}`;

      const labelSpan = document.createElement('span');
      labelSpan.className = 'fabric-swatch__more-label';
      labelSpan.textContent = 'mehr';

      btn.appendChild(countSpan);
      btn.appendChild(labelSpan);
      inlineRow.appendChild(btn);
    }
  }

  /* ─── Drawer-Selektion aktualisieren ─────────────────────────────────── */

  function updateDrawerSelection(drawerEl, selectedFabric) {
    drawerEl.querySelectorAll(SEL_DRAWER_IT).forEach(item => {
      const isSel = item.dataset.fabricDrawerItem === selectedFabric;
      item.classList.toggle('is-selected', isSel);
      item.setAttribute('aria-pressed', String(isSel));
      item.setAttribute('aria-label',
        isSel ? `${selectedFabric} (ausgewählt)` : item.dataset.fabricDrawerItem
      );
    });
  }

  /* ─── Auswahl verarbeiten ─────────────────────────────────────────────── */

  function handleSelect(pickerEl, drawerEl, fabricName, fromDrawer) {
    // 1) Inline-Swatches neu sortieren
    rebuildInlineSwatches(pickerEl, fabricName);

    // 2) Drawer-States
    if (drawerEl) updateDrawerSelection(drawerEl, fabricName);

    // 3) Aktuellen Wert speichern
    pickerEl.dataset.currentFabric = fabricName;

    // 4) option-info Label aktualisieren
    updateOptionInfoLabel(pickerEl, fabricName);

    // 5) Nativen Radio triggern → Prestige updated Gallery + Preis
    triggerNativeRadio(pickerEl, fabricName);

    // 6) Drawer schließen
    if (fromDrawer && drawerEl) {
      setTimeout(() => closeModal(drawerEl), 220);
    }
  }

  /* ─── DOM-Helper ──────────────────────────────────────────────────────── */

  function findDrawer(pickerEl) {
    let el = pickerEl.nextElementSibling;
    while (el) {
      if (el.matches(SEL_DRAWER)) return el;
      el = el.nextElementSibling;
    }
    return document.querySelector(SEL_DRAWER);
  }

  function findPicker(drawerEl) {
    let el = drawerEl.previousElementSibling;
    while (el) {
      if (el.matches(SEL_PICKER)) return el;
      el = el.previousElementSibling;
    }
    return document.querySelector(SEL_PICKER);
  }

  /* ─── Event Delegation ────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {

    // Inline-Swatch (Label mit data-fabric-swatch)
    const swatchLabel = e.target.closest('label[data-fabric-swatch]');
    if (swatchLabel) {
      // Das <label> klickt den Radio-Input automatisch (via for=).
      // Wir müssen nur noch Swatches neu sortieren + Label updaten.
      const pickerEl   = swatchLabel.closest(SEL_PICKER);
      const drawerEl   = pickerEl ? findDrawer(pickerEl) : null;
      const fabricName = swatchLabel.dataset.fabricSwatch;
      if (pickerEl && fabricName) {
        // Kurze Verzögerung damit der Radio gecheckt ist bevor wir neu bauen
        requestAnimationFrame(() => handleSelect(pickerEl, drawerEl, fabricName, false));
      }
      return;
    }

    // "+N mehr" → Drawer öffnen
    const moreBtn = e.target.closest(SEL_MORE);
    if (moreBtn) {
      const pickerEl = moreBtn.closest(SEL_PICKER);
      const drawerEl = pickerEl ? findDrawer(pickerEl) : document.querySelector(SEL_DRAWER);
      if (drawerEl) {
        openModal(drawerEl);
        const current = pickerEl?.dataset.currentFabric;
        if (current) {
          requestAnimationFrame(() => {
            const activeItem = drawerEl.querySelector(
              `[data-fabric-drawer-item="${CSS.escape(current)}"]`
            );
            if (activeItem) activeItem.scrollIntoView({ block: 'center' }), activeItem.focus();
          });
        }
      }
      return;
    }

    // Drawer-Item
    const drawerItem = e.target.closest(SEL_DRAWER_IT);
    if (drawerItem) {
      const drawerEl   = drawerItem.closest(SEL_DRAWER);
      const pickerEl   = drawerEl ? findPicker(drawerEl) : null;
      const fabricName = drawerItem.dataset.fabricDrawerItem;
      if (pickerEl && fabricName) handleSelect(pickerEl, drawerEl, fabricName, true);
      return;
    }
  });

  // Escape schließt Drawer
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    const open = document.querySelector(`${SEL_DRAWER}[open], ${SEL_DRAWER}.is-open`);
    if (open) closeModal(open);
  });

})();
